import { Card, ContentState } from "@whitehaven/ui";
import {
  schedulingResourceKinds,
  type SchedulingCatalogStatus,
} from "./scheduling-contract";
import { AdminSchedulingNavigation } from "./AdminSchedulingNavigation";
import {
  catalogResourceKindsForFocus,
  type AdminSchedulingCatalogFocus,
  type AdminSchedulingCatalogMutationRequest,
  type AdminSchedulingCatalogSnapshot,
  type AdminSchedulingLocation,
  type AdminSchedulingModality,
  type AdminSchedulingResource,
  type AdminSchedulingService,
  type AdminSchedulingStaffSkill,
} from "./scheduling-portal-model";

export interface AdminSchedulingCatalogViewProps {
  readonly focus: AdminSchedulingCatalogFocus;
  readonly catalog: AdminSchedulingCatalogSnapshot;
  readonly canWrite: boolean;
  readonly busy?: boolean;
  readonly feedback?: string | null;
  readonly onMutationRequest: (
    request: AdminSchedulingCatalogMutationRequest,
  ) => void;
}

const catalogDestinations = [
  { focus: "all", label: "All resources", href: "/admin/resources" },
  { focus: "locations", label: "Locations", href: "/admin/locations" },
  { focus: "rooms", label: "Rooms", href: "/admin/rooms" },
  { focus: "stations", label: "Stations", href: "/admin/stations" },
  { focus: "equipment", label: "Equipment", href: "/admin/equipment" },
  { focus: "modalities", label: "Modalities", href: "/admin/modalities" },
  { focus: "services", label: "Services", href: "/admin/services" },
  {
    focus: "staff-skills",
    label: "Staff skills",
    href: "/admin/staff-skills",
  },
] as const;

function nextStatus(status: SchedulingCatalogStatus): SchedulingCatalogStatus {
  return status === "ACTIVE" ? "ARCHIVED" : "ACTIVE";
}

function StatusAction({
  entityKind,
  entity,
  disabled,
  onMutationRequest,
}: {
  readonly entityKind:
    | "location"
    | "resource"
    | "modality"
    | "service"
    | "staff-skill";
  readonly entity:
    | AdminSchedulingLocation
    | AdminSchedulingResource
    | AdminSchedulingModality
    | AdminSchedulingService
    | AdminSchedulingStaffSkill;
  readonly disabled: boolean;
  readonly onMutationRequest: (
    request: AdminSchedulingCatalogMutationRequest,
  ) => void;
}) {
  const status = nextStatus(entity.status);
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => {
        onMutationRequest({
          kind: "SET_CATALOG_STATUS",
          entityKind,
          entityId: entity.id,
          expectedUpdatedAt: entity.updatedAt,
          status,
        });
      }}
    >
      {status === "ACTIVE" ? "Restore" : "Archive"}
    </button>
  );
}

function showSection(
  focus: AdminSchedulingCatalogFocus,
  candidates: readonly AdminSchedulingCatalogFocus[],
): boolean {
  return focus === "all" || candidates.includes(focus);
}

export function AdminSchedulingCatalogView({
  focus,
  catalog,
  canWrite,
  busy = false,
  feedback = null,
  onMutationRequest,
}: AdminSchedulingCatalogViewProps) {
  const resourceKinds = catalogResourceKindsForFocus(focus);
  const resources = catalog.resources.filter((resource) =>
    resourceKinds.includes(resource.kind),
  );
  const showResources = showSection(focus, ["rooms", "stations", "equipment"]);

  return (
    <section
      className="portal-page scheduling-catalog-page"
      aria-labelledby="portal-title"
      aria-describedby="scheduling-catalog-description"
      data-scheduling-catalog-focus={focus}
    >
      <header className="portal-page__header">
        <div>
          <p className="portal-kicker">Scheduling configuration</p>
          <h1 id="portal-title" tabIndex={-1} data-main-focus data-route-focus>
            Resource catalog
          </h1>
          <p id="scheduling-catalog-description">
            Manage locations, assignable resources, modalities, services, staff
            skills, eligibility, and required resource capacity.
          </p>
        </div>
      </header>
      <AdminSchedulingNavigation current={{ kind: "catalog", value: focus }} />
      <nav aria-label="Resource catalog sections">
        <ul>
          {catalogDestinations.map((destination) => (
            <li key={destination.focus}>
              <a
                href={destination.href}
                aria-current={destination.focus === focus ? "page" : undefined}
              >
                {destination.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>
      {!canWrite && (
        <ContentState
          kind="warning"
          title="Catalog is read-only"
          headingLevel={2}
          live={false}
        >
          Your current permissions or the configured gateway do not allow
          catalog changes.
        </ContentState>
      )}
      {feedback && (
        <p role="status" aria-live="polite">
          {feedback}
        </p>
      )}
      {showSection(focus, ["locations"]) && (
        <section aria-labelledby="catalog-locations">
          <h2 id="catalog-locations">Locations ({catalog.locations.length})</h2>
          <div className="scheduling-catalog-grid">
            {catalog.locations.map((location) => (
              <Card
                headingLevel={3}
                eyebrow={location.status}
                title={location.displayName}
                description={`${location.publicLabel} · ${location.timezone}`}
                actions={
                  <StatusAction
                    entityKind="location"
                    entity={location}
                    disabled={!canWrite || busy}
                    onMutationRequest={onMutationRequest}
                  />
                }
                key={location.id}
              />
            ))}
          </div>
        </section>
      )}
      {showResources && (
        <section aria-labelledby="catalog-resources">
          <h2 id="catalog-resources">
            Rooms, stations, equipment, staff, and shared facilities (
            {resources.length})
          </h2>
          <div className="scheduling-catalog-grid">
            {resources.map((resource) => (
              <Card
                headingLevel={3}
                eyebrow={`${resource.kind.replaceAll("_", " ")} · ${resource.status}`}
                title={resource.displayLabel}
                description={
                  catalog.locations.find(
                    (location) => location.id === resource.locationId,
                  )?.displayName ?? "Organization-wide resource"
                }
                actions={
                  <StatusAction
                    entityKind="resource"
                    entity={resource}
                    disabled={!canWrite || busy}
                    onMutationRequest={onMutationRequest}
                  />
                }
                key={resource.id}
              />
            ))}
          </div>
        </section>
      )}
      {showSection(focus, ["modalities"]) && (
        <section aria-labelledby="catalog-modalities">
          <h2 id="catalog-modalities">
            Modalities ({catalog.modalities.length})
          </h2>
          <div className="scheduling-catalog-grid">
            {catalog.modalities.map((modality) => (
              <Card
                headingLevel={3}
                eyebrow={modality.status}
                title={modality.displayName}
                description={`${modality.publicLabel} · ${
                  modality.exclusiveConflict
                    ? "exclusive capacity"
                    : "shared capacity"
                }`}
                actions={
                  <StatusAction
                    entityKind="modality"
                    entity={modality}
                    disabled={!canWrite || busy}
                    onMutationRequest={onMutationRequest}
                  />
                }
                key={modality.id}
              />
            ))}
          </div>
        </section>
      )}
      {showSection(focus, ["services"]) && (
        <section aria-labelledby="catalog-services">
          <h2 id="catalog-services">Services ({catalog.services.length})</h2>
          <div className="scheduling-catalog-grid">
            {catalog.services.map((service) => (
              <Card
                headingLevel={3}
                eyebrow={service.status}
                title={service.displayName}
                description={`${service.defaultDurationMinutes} minutes · lead ${service.minimumLeadMinutes} minutes · advance ${service.maximumAdvanceDays} days`}
                actions={
                  <StatusAction
                    entityKind="service"
                    entity={service}
                    disabled={!canWrite || busy}
                    onMutationRequest={onMutationRequest}
                  />
                }
                footer={`Buffers: setup ${service.setupMinutes}, cleanup ${service.cleanupMinutes}, staff ${service.staffMinutes}, travel ${service.travelMinutes} minutes`}
                key={service.id}
              />
            ))}
          </div>
          <h3>Resource requirements</h3>
          <ul>
            {catalog.resourceRequirements.map((requirement) => {
              const service =
                catalog.services.find(
                  (candidate) => candidate.id === requirement.serviceId,
                )?.displayName ?? "Unknown service";
              return (
                <li
                  key={`${requirement.serviceId}-${requirement.resourceKind}`}
                >
                  <span>
                    {service}: {requirement.quantity}{" "}
                    {requirement.resourceKind.replaceAll("_", " ")}
                  </span>{" "}
                  <button
                    type="button"
                    disabled={!canWrite || busy || requirement.quantity <= 0}
                    aria-label={`Decrease ${requirement.resourceKind} requirement for ${service}`}
                    onClick={() => {
                      onMutationRequest({
                        kind: "SET_RESOURCE_REQUIREMENT",
                        serviceId: requirement.serviceId,
                        resourceKind: requirement.resourceKind,
                        expectedRevision: requirement.revision,
                        quantity: Math.max(0, requirement.quantity - 1),
                      });
                    }}
                  >
                    Decrease
                  </button>{" "}
                  <button
                    type="button"
                    disabled={!canWrite || busy}
                    aria-label={`Increase ${requirement.resourceKind} requirement for ${service}`}
                    onClick={() => {
                      onMutationRequest({
                        kind: "SET_RESOURCE_REQUIREMENT",
                        serviceId: requirement.serviceId,
                        resourceKind: requirement.resourceKind,
                        expectedRevision: requirement.revision,
                        quantity: requirement.quantity + 1,
                      });
                    }}
                  >
                    Increase
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      )}
      {showSection(focus, ["staff-skills"]) && (
        <section aria-labelledby="catalog-staff-skills">
          <h2 id="catalog-staff-skills">
            Staff skills ({catalog.staffSkills.length})
          </h2>
          <div className="scheduling-catalog-grid">
            {catalog.staffSkills.map((skill) => (
              <Card
                headingLevel={3}
                eyebrow={skill.status}
                title={skill.displayLabel}
                actions={
                  <StatusAction
                    entityKind="staff-skill"
                    entity={skill}
                    disabled={!canWrite || busy}
                    onMutationRequest={onMutationRequest}
                  />
                }
                key={skill.id}
              />
            ))}
          </div>
          <h3>Service eligibility</h3>
          <ul>
            {catalog.serviceEligibilities.map((eligibility) => {
              const service =
                catalog.services.find(
                  (candidate) => candidate.id === eligibility.serviceId,
                )?.displayName ?? "Unknown service";
              const skill =
                catalog.staffSkills.find(
                  (candidate) => candidate.id === eligibility.staffSkillId,
                )?.displayLabel ?? "Unknown skill";
              return (
                <li
                  key={`${eligibility.serviceId}-${eligibility.staffSkillId}`}
                >
                  <span>
                    {service} · {skill}:{" "}
                    {eligibility.eligible ? "eligible" : "not eligible"}
                  </span>{" "}
                  <button
                    type="button"
                    disabled={!canWrite || busy}
                    onClick={() => {
                      onMutationRequest({
                        kind: "SET_SERVICE_ELIGIBILITY",
                        serviceId: eligibility.serviceId,
                        staffSkillId: eligibility.staffSkillId,
                        expectedRevision: eligibility.revision,
                        eligible: !eligibility.eligible,
                      });
                    }}
                  >
                    Mark {eligibility.eligible ? "not eligible" : "eligible"}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      )}
      {focus === "all" && (
        <section aria-labelledby="catalog-resource-kinds">
          <h2 id="catalog-resource-kinds">Supported resource kinds</h2>
          <p>
            {schedulingResourceKinds
              .map((kind) => kind.replaceAll("_", " "))
              .join(", ")}
          </p>
        </section>
      )}
    </section>
  );
}
